// @ts-check
/**
 * Coordination — the names one coordination shares across an origin.
 *
 * The election, the budget and the queue are `Coordinator`'s
 * (`classes/coordination/coordinator.js`). What lives here is the
 * vocabulary every context of one coordination has to agree on without holding
 * a coordinator: the topic and the protocol word on the wire, the default cap,
 * and the grammar of a **claim** — the Web Lock a context takes before it asks
 * for a slot, whose name carries everything a rebuilding leader needs to read
 * the origin's held locks back into a ledger.
 *
 * ```text
 * <name>/claim/<kind>/<weight>/<contextId>/<serial>
 * ```
 *
 * @module helpers/coordination
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1, Claude Opus 5 (code generation)
 */

/** The topic the leader's state is published on when an application names none. */
export const DEFAULT_TOPIC = 'coordination';

/** What every message on the wire carries, so a foreign one is ignored rather than misread. */
export const PROTOCOL = 'coordination/1';

/** Two concurrent heavy workers, not more — where the applications settled (W2). */
export const DEFAULT_CAP = 2;

/**
 * The prefix every claim of one coordination shares — what a rebuild looks for
 * among the origin's held locks.
 *
 * @param {string} name
 */
export function claimPrefixFor(name) {
    return `${name}/claim/`;
}

/**
 * A claim's lock name: `<name>/claim/<kind>/<weight>/<contextId>/<serial>`.
 *
 * The kind and the weight are IN THE NAME so that a leader taking over from one
 * that died can read the whole ledger off `navigator.locks.query()` — the
 * browser already knows which claims are alive, and this is what makes that
 * knowledge legible.
 *
 * @param {{ name: string, kind: string, weight: number, contextId: string, serial: number }} given
 */
export function claimNameFor({ name, kind, weight, contextId, serial }) {
    return `${claimPrefixFor(name)}${kind}/${weight}/${contextId}/${serial}`;
}

/**
 * The reverse of `claimNameFor`, refusing anything that is not one of ours —
 * an origin's locks belong to everything on it, and a name this does not
 * recognise is somebody else's business.
 *
 * @param {string} lockName
 * @param {string} prefix from `claimPrefixFor`
 * @returns {{ claim: string, kind: string, weight: number, contextId: string } | null}
 */
export function parseClaimName(lockName, prefix) {
    if (typeof lockName !== 'string' || !lockName.startsWith(prefix)) return null;
    const parts = lockName.slice(prefix.length).split('/');
    if (parts.length !== 4) return null;
    const [kind, weightText, contextId, serial] = parts;
    const weight = Number(weightText);
    if (!kind || !contextId || !serial || !Number.isInteger(weight) || weight < 1) return null;
    return { claim: lockName, kind, weight, contextId };
}
